import React, { useState, useEffect, useContext } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";
import { Table, Button, Badge, Modal, Form, Card, Row, Col, Alert, Pagination, Spinner } from "react-bootstrap";
import Select from 'react-select';

const PaymentManagement = () => {
  // Quản lý file mới chọn từ máy tính (Hàng chờ submit)
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [statementFiles, setStatementFiles] = useState([]);

  // Quản lý các file đã có sẵn trên database khi mở form EDIT
  const [existingFiles, setExistingFiles] = useState({
    invoices: [],  // Mảng chứa các object file từ server
    statements: []
  });

  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const { user } = useContext(UserContext);
  const storedUser = localStorage.getItem("user");
  const currentUser = user || (storedUser ? JSON.parse(storedUser) : null);

  const [paymentList, setPaymentList] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialFormData = {
    paymentCode: "",
    title: "",
    requestDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    departmentId: "",
    costOwnership: "",
    reason: "test",
    paymentReasonDetails: "",
    costType: "",
    projectId: "",
    orderCode: "",
    paymentTarget: 0,
    supplierId: "",
    beneficiaryName: "",
    bankAccountNumber: "",
    bankName: "",
    transferContent: "",
    documentType: "",
    totalAmount: 0,
    currency: "VND",
    businessUnit: "",
    internalDocumentType: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertStatus, setAlertStatus] = useState({ type: "", message: "" });

  // --- Fetch API Lists ---
  const fetchPaymentRequests = async () => {
    setIsLoadingList(true);
    try {
      const uId = currentUser?.id || currentUser?.userId;
      if (!uId) return;

      const endpoint = API_ENDPOINTS.paymentRequests || "/api/PaymentRequests";
      const response = await axiosClient.get(endpoint);
      
      const myData = (response.data || []).filter(
        (item) => String(item.createdById) === String(uId)
      );
      setPaymentList(myData);
    } catch (error) {
      console.error("❌ [FETCH PAYMENT LIST ERROR]", error);
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.departments);
      setDepartments(res.data || []);
    } catch (error) {
      console.error("❌ Error fetching departments:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.projects);
      setProjects(res.data || []);
    } catch (error) {
      console.error("❌ Error fetching projects:", error);
    }
  };

  const fetchSuppliers = async (type) => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.getSuppliersByType(type)); 
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("❌ Error fetching suppliers:", error);
      setSuppliers([]);
    }
  };

  useEffect(() => {
    fetchPaymentRequests();
    fetchDepartments();
    fetchProjects();
  }, []);

  useEffect(() => {
    if (formData.paymentTarget !== undefined && formData.paymentTarget !== "") {
      fetchSuppliers(parseInt(formData.paymentTarget));
    }
  }, [formData.paymentTarget]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };

    if (name === "paymentTarget") {
      updatedData = {
        ...updatedData,
        supplierId: "",
        beneficiaryName: "",
        bankAccountNumber: "",
        bankName: "",
        transferContent: ""
      };
    }

    if (name === "supplierId") {
      const selectedSupplier = suppliers.find((s) => String(s.id) === String(value));
      if (selectedSupplier) {
        updatedData = {
          ...updatedData,
          beneficiaryName: selectedSupplier.beneficiaryName || "",
          bankAccountNumber: selectedSupplier.bankAccountNumber || "",
          bankName: selectedSupplier.bankName || "",
          transferContent: selectedSupplier.transferContent || ""
        };
      } else {
        updatedData = {
          ...updatedData,
          beneficiaryName: "",
          bankAccountNumber: "",
          bankName: "",
          transferContent: ""
        };
      }
    }

    setFormData(updatedData);

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    const fieldsToWatch = ['businessUnit', 'beneficiaryName', 'totalAmount', 'currency', 'paymentCode'];
    if (fieldsToWatch.includes(name) || name === 'supplierId') {
      const { paymentCode, businessUnit, beneficiaryName, totalAmount, currency } = updatedData;
      const currencyMap = { "VND": "Vietnamese Dong", "AUD": "Australian Dollar" };
      const currencyLabel = currencyMap[currency] || currency || "VND";
      const displayAmount = totalAmount || "0";
      
      const newTitle = `DNTT ${paymentCode || "..."}  ${businessUnit || "..."}  ${beneficiaryName || "..."}  ${displayAmount} ${currencyLabel}`;
      setFormData((prev) => ({ ...prev, title: newTitle }));
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    let sanitized = value.replace(/[^0-9.,]/g, "");
    let processedValue = sanitized.split(".").join(""); 
    processedValue = processedValue.replace(",", ".");   

    setFormData({ ...formData, totalAmount: processedValue });
  };

  const formatInputAmount = (value) => {
    if (!value) return "";
    let val = String(value);
    let parts = val.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return parts.length > 1 ? parts.join(",") : parts[0] + (val.includes(".") ? "," : "");
  };

  // --- File Logic ---
  const handleFileChange = (e, fileType) => {
    const selectedFiles = Array.from(e.target.files);
    if (fileType === "invoice") {
      setInvoiceFiles(prev => [...prev, ...selectedFiles]);
    } else if (fileType === "statement") {
      setStatementFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const handleRemoveQueuedFile = (index, type) => {
    if (type === "invoice") {
      setInvoiceFiles(prev => prev.filter((_, i) => i !== index));
    } else if (type === "statement") {
      setStatementFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Hàm bổ sung: Xóa file cũ đã lưu trên DB (khi Edit) ra khỏi UI giao diện
  const handleRemoveExistingFile = (id, type) => {
    setExistingFiles(prev => ({
      ...prev,
      [type === "invoice" ? "invoices" : "statements"]: prev[type === "invoice" ? "invoices" : "statements"].filter(f => f.id !== id)
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.requestDate) newErrors.requestDate = "Proposal date is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    if (!formData.departmentId) newErrors.departmentId = "Department is required";
    if (!formData.costOwnership) newErrors.costOwnership = "Payment category is required";
    if (!formData.reason?.toString().trim()) newErrors.reason = "Payment reason description is required";
    if (!formData.costType) newErrors.costType = "Cost type is required";
    if (!formData.projectId) newErrors.projectId = "Project is required";
    if (!formData.orderCode?.toString().trim()) newErrors.orderCode = "Order / WO number is required";
    if (formData.paymentTarget === null || formData.paymentTarget === undefined || formData.paymentTarget === "") {
      newErrors.paymentTarget = "Payment target is required";
    }
    if (!formData.beneficiaryName?.toString().trim()) newErrors.beneficiaryName = "Beneficiary name is required";
    if (!formData.bankAccountNumber?.toString().trim()) newErrors.bankAccountNumber = "Account number is required";
    if (!formData.bankName?.toString().trim()) newErrors.bankName = "Bank name is required";
    if (!formData.documentType) newErrors.documentType = "Document type is required";
    if (!formData.businessUnit) newErrors.businessUnit = "Business unit is required";
    if (!formData.internalDocumentType) newErrors.internalDocumentType = "Internal document type is required";

    // Validate ít nhất phải có 1 file Invoice (Cũ hoặc Mới)
    const hasExistingInvoices = existingFiles?.invoices && existingFiles.invoices.length > 0;
    const hasNewInvoices = invoiceFiles && invoiceFiles.length > 0;

    if (!hasExistingInvoices && !hasNewInvoices) {
      newErrors.invoice = "At least one Attached Invoice file is required";
    }

    if (Number(formData.totalAmount) <= 0) {
      newErrors.totalAmount = "Amount must be greater than 0";
    }

    setErrors(newErrors); 
    return Object.keys(newErrors).length === 0;
  };

  // --- Edit Mode Mode ---
  const handleEditClick = async (id) => {
    setAlertStatus({ type: "", message: "" });
    setErrors({});
    try {
      const endpoint = API_ENDPOINTS.getPaymentRequestById ? API_ENDPOINTS.getPaymentRequestById(id) : `/api/PaymentRequests/${id}`;
      const response = await axiosClient.get(endpoint);
      
      if (response.data) {
        const item = response.data;

        let mappedInvoices = [];
        let mappedStatements = [];

        // ĐÃ SỬA: Kiểm tra theo kiểu Số (0 và 1) dựa theo đúng JSON thực tế từ Backend trả về
        if (item.paymentRequestFiles && item.paymentRequestFiles.length > 0) {
          mappedInvoices = item.paymentRequestFiles.filter(f => f.fileType === 0); 
          mappedStatements = item.paymentRequestFiles.filter(f => f.fileType === 1);
        } 

        setExistingFiles({ 
          invoices: mappedInvoices, 
          statements: mappedStatements
        });

        setInvoiceFiles([]);    
        setStatementFiles([]);  

        const targetMap = { "Supplier": 0, "Customer": 1, "Internal": 2 };
        const resolvedPaymentTarget = typeof item.paymentTarget === 'string' 
          ? (targetMap[item.paymentTarget] ?? "") 
          : item.paymentTarget;

        setFormData({
          id: item.id,
          title: item.title || "",
          paymentCode: item.paymentCode || "",
          requestDate: item.requestDate ? item.requestDate.split("T")[0] : new Date().toISOString().split("T")[0],
          dueDate: item.dueDate ? item.dueDate.split("T")[0] : "",
          departmentId: item.departmentId || "",
          costOwnership: item.costOwnership || "",
          reason: item.reason || "",
          paymentReasonDetails: item.paymentReasonDetails || "",
          costType: item.costType || "",
          projectId: item.projectId || "",
          orderCode: item.orderCode || "",
          paymentTarget: resolvedPaymentTarget,
          supplierId: item.supplierId || "",
          beneficiaryName: item.beneficiaryName || item.beneficiary || "",
          bankAccountNumber: item.bankAccountNumber || item.accountNumber || "",
          bankName: item.bankName || "",
          transferContent: item.transferContent || "",
          documentType: item.documentType || "",
          totalAmount: item.totalAmount || 0,
          currency: item.currency || "VND",
          businessUnit: item.businessUnit || "",
          internalDocumentType: item.internalDocumentType || "",
        });
        
        setEditingId(id);
        setShowCreateModal(true);
      }
    } catch (error) {
      console.error("❌ [FETCH DETAIL FOR EDIT ERROR]", error);
    }
  };

  const handleOpenCreateModal = async () => {
    setFormData(initialFormData);
    setInvoiceFiles([]);
    setStatementFiles([]);
    setExistingFiles({ invoices: [], statements: [] });
    setErrors({});
    
    try {
      const response = await axiosClient.get(API_ENDPOINTS.getNextPaymentCode);
      setFormData(prev => ({ ...prev, paymentCode: response.data }));
    } catch (error) {
      console.error("❌ Không thể lấy mã Payment Code mới:", error);
    }
    setShowCreateModal(true);
  };

  // --- Submit form ---
  const handleSubmit = async (e) => {
  if (e) e.preventDefault();
  setAlertStatus({ type: "", message: "" });

  // --- 🛑 BỔ SUNG: KIỂM TRA DUNG LƯỢNG FILE TRƯỚC KHI SUBMIT ---
  const MAX_FILE_SIZE_MB = 30; // Đặt hạn mức an toàn là 30MB
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  // Gom toàn bộ file mới đang chuẩn bị upload
  const allNewFiles = [
    ...(invoiceFiles || []),
    ...(statementFiles || [])
  ];

  // Tính tổng dung lượng (bytes) của tất cả file mới cộng lại
  const totalBytes = allNewFiles.reduce((sum, file) => sum + (file.size || 0), 0);

  // Nếu vượt quá hạn mức, chặn lại luôn ní ơi và bắn alert ra màn hình
  if (totalBytes > MAX_FILE_SIZE_BYTES) {
    const totalMB = (totalBytes / (1024 * 1024)).toFixed(2);
    const msg = `❌ Tổng dung lượng file đính kèm quá lớn (${totalMB} MB). Hệ thống chỉ cho phép tải lên tối đa ${MAX_FILE_SIZE_MB} MB để đảm bảo kết nối ổn định. Vui lòng giảm bớt file hoặc nén dung lượng trước khi gửi!`;
    
    alert(msg);
    setAlertStatus({
      type: "danger",
      message: msg,
    });
    return; // Dừng xử lý tại đây, không tốn tài nguyên gửi lên server
  }
  // -------------------------------------------------------------

  if (!validateForm()) return; 

  setIsSubmitting(true);
  const dataToSend = new FormData();
  
  Object.keys(formData).forEach((key) => {
    const value = formData[key];
    if (value !== null && value !== undefined && value !== "") {
      dataToSend.append(key, value);
    }
  });

  // Loop duy nhất 1 lần sạch sẽ cho Invoice Files
  invoiceFiles.forEach((file) => {
    dataToSend.append("InvoiceFiles", file); 
  });

  // Loop duy nhất 1 lần sạch sẽ cho Statement Files
  statementFiles.forEach((file) => {
    dataToSend.append("StatementFiles", file); 
  });

  // Append danh sách các ID file cũ còn được giữ lại (nếu đang ở chế độ Edit)
  if (editingId) {
    if (existingFiles?.invoices && Array.isArray(existingFiles.invoices)) {
      existingFiles.invoices.forEach(f => { if(f?.id) dataToSend.append("KeepFileIds", f.id) });
    }
    if (existingFiles?.statements && Array.isArray(existingFiles.statements)) {
      existingFiles.statements.forEach(f => { if(f?.id) dataToSend.append("KeepFileIds", f.id) });
    }
  }
  
  if (currentUser?.id || currentUser?.userId) {
    dataToSend.append("CreatedById", currentUser.id || currentUser.userId);
  }

  try {
    let response;
    if (editingId) {
      const targetUrl = API_ENDPOINTS.updatePaymentRequest ? API_ENDPOINTS.updatePaymentRequest(editingId) : `/api/PaymentRequests/${editingId}`;
      response = await axiosClient.put(targetUrl, dataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAlertStatus({ type: "success", message: "Payment request updated successfully!" });
    } else {
      const targetUrl = API_ENDPOINTS.paymentRequests || "/api/PaymentRequests";
      response = await axiosClient.post(targetUrl, dataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAlertStatus({ type: "success", message: "Payment request submitted successfully!" });
    }
    
    setShowCreateModal(false);
    setEditingId(null);
    setFormData(initialFormData); 
    setErrors({}); 
    fetchPaymentRequests();
 } catch (error) {
    console.error("❌ [PAYMENT SUBMIT ERROR]", error);
    
    // 1. Mặc định nếu không bắt được lỗi cụ thể từ server
    let backendMessage = "An error occurred while processing your request.";

    // 2. Kiểm tra và bóc tách dữ liệu lỗi từ Backend trả về
    if (error.response && error.response.data) {
      if (typeof error.response.data === "string") {
        backendMessage = error.response.data;
      } else if (error.response.data.message) {
        backendMessage = error.response.data.message;
      } else if (error.response.data.errors) {
        backendMessage = Object.values(error.response.data.errors).flat().join(" ");
      }
    } else if (error.message === "Network Error") {
      // Đề phòng trường hợp hiếm gặp nếu file lọt lưới (hoặc config server quá thấp), thông báo dễ hiểu cho user
      backendMessage = "Lỗi kết nối mạng (Network Error). Dung lượng file gửi đi có thể quá lớn hoặc Server từ chối phản hồi.";
    }

    // BẬT ALERT THÔNG BÁO LÊN MÀN HÌNH LUÔN NÍ ƠI
    alert(backendMessage);

    setAlertStatus({
      type: "danger",
      message: backendMessage,
    });

  } finally {
    setIsSubmitting(false);
  }
};

  // --- UI Helpers ---
  const handleDeleteClick = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete payment request: "${title}"?`)) {
      setAlertStatus({ type: "", message: "" });
      try {
        const endpoint = API_ENDPOINTS.deletePaymentRequest ? API_ENDPOINTS.deletePaymentRequest(id) : `/api/PaymentRequests/${id}`;
        await axiosClient.delete(endpoint);
        setAlertStatus({ type: "success", message: "Payment request deleted successfully!" });
        fetchPaymentRequests();
      } catch (error) {
        console.error("❌ [DELETE PAYMENT REQUEST ERROR]", error);
        setAlertStatus({
          type: "danger",
          message: error.response?.data?.message || "An error occurred while deleting the request.",
        });
      }
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      0: { bg: "secondary", text: "DRAFT" },
      1: { bg: "warning", text: "PENDING", color: "dark" },
      2: { bg: "success", text: "APPROVED" },
      3: { bg: "danger", text: "REJECTED" },
    };
    const config = badges[status] || { bg: "info", text: "UNKNOWN" };
    return <Badge bg={config.bg} text={config.color || "white"}>{config.text}</Badge>;
  };
  
  const formatCurrency = (amount, currency = "VND") => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0, 
      maximumFractionDigits: 3, 
    }).format(numericAmount);
  };

   const getFullName = (userData) => {
  if (!userData) return null;
  // Dữ liệu của bạn có employeeName ở cấp cao nhất
  return userData.employeeName || userData.fullName || null;
};

const getDownloadUrl = (path) => {
  if (!path) return "#";
  const baseUrl = API_ENDPOINTS.url.replace(/\/$/, ""); // Xóa dấu / cuối nếu có
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Nếu là local thì dùng đường dẫn gốc, nếu là server thì chèn /api
  return baseUrl.includes("localhost") 
    ? `${baseUrl}${cleanPath}` 
    : `${baseUrl}/api${cleanPath}`;
};


  const renderStatusBadge = (status) => {
    let badgeClass = "bg-secondary text-white";
    if (status === "Approved") badgeClass = "bg-success text-white";
    else if (status === "Pending") badgeClass = "bg-warning text-dark";
    else if (status === "Rejected") badgeClass = "bg-danger text-white";

    return (
      <span className={`badge ${badgeClass} px-3 py-2 font-weight-bold`} style={{ borderRadius: "12px", fontSize: "0.75rem" }}>
        {status || "Pending"}
      </span>
    );
  };

 return (
    <div className="container-fluid mb-5 px-4">
      {/* --- HEADER --- */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4 mt-4 pb-3 border-bottom">
        <div>
          <h1 className="h3 mb-1 text-gray-800 font-weight-bold text-uppercase tracking-wide">Payment Management</h1>
          <p className="text-muted small mb-0">
            Create, edit and track financial payment requests (Quản lý và theo dõi đơn đề nghị thanh toán)
          </p>
        </div>
        <button 
          className="btn btn-primary font-weight-bold px-4 py-2 shadow-sm border-0 bg-gradient-primary" 
          style={{ borderRadius: "20px", transition: "all 0.3s" }}
          onClick={handleOpenCreateModal}
        >
          <i className="fas fa-plus mr-2"></i> Create New Request
        </button>
      </div>

      {alertStatus.message && (
        <div className={`alert alert-${alertStatus.type} alert-dismissible fade show shadow-sm border-0 mb-4`} role="alert" style={{ borderRadius: "8px" }}>
          <i className={`fas ${alertStatus.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"} mr-2`}></i>
          {alertStatus.message}
          <button type="button" className="close" onClick={() => setAlertStatus({ type: "", message: "" })}>&times;</button>
        </div>
      )}

      {/* --- DATA GRID BÊN NGOÀI --- */}
      <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
        <div className="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-dark text-uppercase small tracking-wider">
            <i className="fas fa-list mr-2 text-primary"></i>Payment Request List
          </h6>
          <button className="btn btn-sm btn-light border-0" onClick={fetchPaymentRequests}>
            <i className={`fas fa-sync-alt ${isLoadingList ? "fa-spin" : ""}`}></i>
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle" style={{ minWidth: "1100px" }}>
              <thead className="text-muted font-weight-bold text-uppercase small" style={{ backgroundColor: "#f8f9fc" }}>
                <tr>
                  <th className="py-3 text-center" style={{ width: "50px" }}>#</th>
                  <th className="pl-4 py-3">Title</th>
                  <th className="py-3">Created By</th>
                  <th className="py-3">Current Approver</th>
                  <th className="py-3 text-center">Amount</th>
                  <th className="py-3 text-center">Status</th>
                  <th className="py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="small text-dark">
                {isLoadingList ? (
                  <tr><td colSpan="7" className="text-center py-5">Loading...</td></tr>
                ) : paymentList.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-4 text-muted">No records found</td></tr>
                ) : (
                  paymentList.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="text-center py-3 font-weight-bold text-muted">{index + 1}</td>
                      
                      <td className="pl-4 py-3">
                        <span className="d-block text-dark font-weight-bold">{item.title}</span>
                        <small className="text-muted">Due: {item.dueDate ? new Date(item.dueDate).toLocaleDateString("vi-VN") : "N/A"}</small>
                      </td>
                      
                      <td className="py-3 font-weight-medium">
                        {getFullName(item.createdBy)}
                      </td>

                      <td className="py-3">
                        {item.approvalLogs && item.approvalLogs.length > 0 ? (
                          <div className="d-flex flex-column" style={{ gap: '5px' }}>
                            {[...item.approvalLogs].sort((a, b) => a.stepOrder - b.stepOrder).map((log, idx) => (
                              <div key={idx} className={`p-2 border-left border-3 rounded bg-light mb-1 ${log.status === 'Rejected' ? 'border-danger' : 'border-info'}`}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="font-weight-bold text-dark" style={{ fontSize: '0.75rem' }}>
                                    {log.approver?.employeeLeaveDetail?.fullName || log.approverName || "System"}
                                  </span>
                                  <Badge bg={log.status === 'Approved' ? 'success' : log.status === 'Rejected' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                                    {log.status}
                                  </Badge>
                                </div>
                                {log.comment && (
                                  <div className="text-muted mt-1 font-italic" style={{ fontSize: '0.7rem' }}>
                                    <strong>Note:</strong> {log.comment}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">---</span>
                        )}
                        <small className="text-muted d-block mt-1">Current Step: {item.currentStep ?? 0}</small>
                      </td>

                      <td className="text-right font-weight-bold py-3">
                        {formatCurrency(item.totalAmount, item.currency)}
                      </td>

                      <td className="text-center py-3">
                         {getStatusBadge(item.status)}
                      </td>
                      
                      <td className="text-center py-3">
                        <button className="btn btn-sm btn-outline-primary border-0 rounded-circle mr-1" onClick={() => handleEditClick(item.id)}><i className="fas fa-edit"></i></button>
                        <button className="btn btn-sm btn-outline-danger border-0 rounded-circle" onClick={() => handleDeleteClick(item.id, item.title)}><i className="fas fa-trash-alt"></i></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- POPUP MODAL CHỨA FORM LẬP / SỬA PHIẾU CHI --- */}
      {showCreateModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)", overflowY: "auto" }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: "12px" }}>
              <div className="modal-header bg-gradient-primary text-white py-3">
                <h5 className="modal-title font-weight-bold">
                  <i className="fas fa-file-invoice-dollar mr-2"></i>
                  {editingId ? "Update Payment Request Details" : "Create New Payment Request"}
                </h5>
                <button type="button" className="close text-white" onClick={() => { setShowCreateModal(false); setEditingId(null); }}>&times;</button>
              </div>
              
              <div className="modal-body px-4 py-3">
                <form id="paymentForm" onSubmit={handleSubmit}>
                  
                  {/* --- Section 1: General Information --- */}
                  <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3">General Information (Thông tin chung)</h6>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label font-weight-bold small">Proposal Date <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Ngày đề xuất)</span></label>
                      <input type="date" className="form-control form-control-sm" name="requestDate" value={formData.requestDate || ''} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label font-weight-bold small">Due Date <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Ngày đến hạn)</span></label>
                      <input type="date" className={`form-control form-control-sm ${errors.dueDate ? "is-invalid" : ""}`} name="dueDate" value={formData.dueDate || ''} onChange={handleInputChange} />
                      {errors.dueDate && <div className="invalid-feedback">{errors.dueDate}</div>}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">
                        Department <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Bộ phận)</span>
                      </label>
                      <select 
                        className={`form-control form-control-sm ${errors.departmentId ? "is-invalid" : ""}`} 
                        name="departmentId" 
                        value={formData.departmentId || ''} 
                        onChange={handleInputChange}
                      >
                        <option value="">(not set)</option>
                        {departments && departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                      {errors.departmentId && <div className="invalid-feedback">{errors.departmentId}</div>}
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Payment Category <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Chi phí trực thuộc)</span></label>
                      <select className={`form-control form-control-sm ${errors.costOwnership ? "is-invalid" : ""}`} name="costOwnership" value={formData.costOwnership || ''} onChange={handleInputChange}>
                        <option value="">(not set)</option>
                        <option value="Chung">General (Chung)</option>
                        <option value="Bộ phận">Department (Bộ phận)</option>
                        <option value="Đơn hàng">Order (Đơn hàng)</option>
                      </select>
                      {errors.costOwnership && <div className="invalid-feedback">{errors.costOwnership}</div>}
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Cost Type <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Loại chi phí)</span></label>
                      <select className={`form-control form-control-sm ${errors.costType ? "is-invalid" : ""}`} name="costType" value={formData.costType || ''} onChange={handleInputChange}>
                        <option value="">(not set)</option>
                        <option value="CPVT">CPVT: Chi phí Vật Tư</option>
                        <option value="CPNC">CPNC: Chi phí nhân công</option>
                        <option value="CPVC">CPVC: Chi phí vận chuyển</option>
                        <option value="CPBH">CPBH: Chi phí bán hàng</option>
                        <option value="CPQL">CPQL: Chi phí quản lý</option>
                        <option value="CPDP">CPDP: Chi phí định phí</option>
                        <option value="CPTAX">CPTAX: Chi phí thuế</option>
                        <option value="CPKh">CPKh: Chi phí khác</option>
                      </select>
                      {errors.costType && <div className="invalid-feedback">{errors.costType}</div>}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <input type="hidden" name="reason" value={formData.reason || "test"} />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label font-weight-bold small">
                        Project <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Dự án)</span>
                      </label>
                      <select 
                        className={`form-control form-control-sm ${errors.projectId ? "is-invalid" : ""}`} 
                        name="projectId" 
                        value={formData.projectId || ""} 
                        onChange={handleInputChange}
                      >
                        <option value="">-- Chọn dự án --</option>
                        {projects && projects.map((proj) => (
                          <option key={proj.id} value={proj.id}>
                            {proj.code} - {proj.name}
                          </option>
                        ))}
                      </select>
                      {errors.projectId && <div className="invalid-feedback">{errors.projectId}</div>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Business Unit <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Business unit)</span></label>
                      <select className={`form-control form-control-sm ${errors.businessUnit ? "is-invalid" : ""}`} name="businessUnit" value={formData.businessUnit || ''} onChange={handleInputChange}>
                        <option value="">(not set)</option>
                        <option value="PEX">PEX</option>
                        <option value="GOZA">GOZA</option>
                        <option value="LUXCOM">LUXCOM</option>
                        <option value="PICON">PICON</option>
                        <option value="PICE">PICE</option>
                      </select>
                      {errors.businessUnit && <div className="invalid-feedback">{errors.businessUnit}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label font-weight-bold small">Purchase Order No <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Đơn hàng)</span></label>
                      <input type="text" className={`form-control form-control-sm ${errors.orderCode ? "is-invalid" : ""}`} name="orderCode" value={formData.orderCode || ''} onChange={handleInputChange} />
                      <small className="form-text text-danger font-weight-bold" style={{ fontSize: '0.75rem' }}>Lưu ý: Vui lòng ghi rõ mã Đơn hàng/Mã WO (Các mã này được quản lý bởi SCM)</small>
                      {errors.orderCode && <div className="invalid-feedback">{errors.orderCode}</div>}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 mb-3">
                      <label className="form-label font-weight-bold small">Payment Reason Details <span className="text-muted font-weight-normal">(Chi tiết lý do bổ sung)</span></label>
                      <textarea className="form-control form-control-sm" name="paymentReasonDetails" rows="2" value={formData.paymentReasonDetails || ''} onChange={handleInputChange} placeholder="Detailed explanation..."></textarea>
                    </div>
                  </div>

                  {/* --- Section 2: Beneficiary & Bank Details --- */}
                  <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Beneficiary & Bank Details (Đối tượng & Thông tin tài khoản)</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">
                        Payment Target <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Đối tượng Thanh toán)</span>
                      </label>
                      <select 
                        className={`form-control form-control-sm ${errors.paymentTarget ? "is-invalid" : ""}`} 
                        name="paymentTarget" 
                        value={formData.paymentTarget ?? ''} 
                        onChange={handleInputChange}
                      >
                        <option value="">(not set)</option>
                        <option value={0}>Supplier (Nhà cung cấp)</option>
                        <option value={1}>Customer (Khách hàng)</option>
                        <option value={2}>Internal (Nội bộ)</option>
                      </select>
                      {errors.paymentTarget && <div className="invalid-feedback">{errors.paymentTarget}</div>}
                    </div>

     <div className="col-md-4 mb-3">
  <label className="form-label font-weight-bold small">
    Nhà cung cấp <span className="text-muted font-weight-normal">(Chọn công ty)</span>
  </label>
  <Select
    classNamePrefix="react-select"
    // Loại bỏ className "is-invalid" ở đây vì nó không có tác dụng với react-select
    options={suppliers ? suppliers.map(sup => ({ value: sup.id, label: sup.name })) : []}
    // Sử dụng == thay vì === để tránh lệch kiểu dữ liệu (String vs Number) khi tìm kiếm label
    value={suppliers && formData.supplierId ? { 
      value: formData.supplierId, 
      label: suppliers.find(s => String(s.id) === String(formData.supplierId))?.name || "" 
    } : null}
    onChange={(selectedOption) => {
      handleInputChange({
        target: {
          name: 'supplierId',
          value: selectedOption ? selectedOption.value : ''
        }
      });
    }}
    placeholder="-- Gõ để tìm nhà cung cấp --"
    isClearable
    styles={{
      control: (base, state) => ({
        ...base,
        minHeight: '31px',
        height: '31px',
        borderRadius: '0.2rem',
        // Sửa lỗi hiển thị viền đỏ (is-invalid) trực tiếp thông qua thuộc tính border của cấu hình style
        borderColor: errors.supplierId ? '#e74a3b' : state.isFocused ? '#bac8f3' : '#d1d3e2',
        boxShadow: state.isFocused && !errors.supplierId ? '0 0 0 0.2rem rgba(78, 115, 223, 0.25)' : null,
        '&:hover': {
          borderColor: errors.supplierId ? '#e74a3b' : state.isFocused ? '#bac8f3' : '#b7b9cc'
        }
      }),
      valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
        fontSize: '0.8rem'
      }),
      input: (base) => ({
        ...base,
        margin: '0px'
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: '29px'
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: '4px'
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: '4px'
      }),
      menu: (base) => ({
        ...base,
        fontSize: '0.8rem',
        zIndex: 9999 // Đảm bảo menu đổ xuống không bị che khuất bởi Modal
      })
    }}
  />
  {/* Đảm bảo thông báo lỗi hiển thị rõ ràng bên dưới */}
  {errors.supplierId && (
    <div className="text-danger small mt-1" style={{ fontSize: '0.75rem', display: 'block' }}>
      {errors.supplierId}
    </div>
  )}
</div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Beneficiary Name <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Thụ hưởng)</span></label>
                      <input type="text" className={`form-control form-control-sm ${errors.beneficiaryName ? "is-invalid" : ""}`} name="beneficiaryName" value={formData.beneficiaryName || ''} onChange={handleInputChange} />
                      {errors.beneficiaryName && <div className="invalid-feedback">{errors.beneficiaryName}</div>}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Account Number <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Số tài khoản)</span></label>
                      <input type="text" className={`form-control form-control-sm ${errors.bankAccountNumber ? "is-invalid" : ""}`} name="bankAccountNumber" value={formData.bankAccountNumber || ''} onChange={handleInputChange} />
                      {errors.bankAccountNumber && <div className="invalid-feedback">{errors.bankAccountNumber}</div>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Bank Name <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Ngân hàng)</span></label>
                      <input type="text" className={`form-control form-control-sm ${errors.bankName ? "is-invalid" : ""}`} name="bankName" value={formData.bankName || ''} onChange={handleInputChange} />
                      {errors.bankName && <div className="invalid-feedback">{errors.bankName}</div>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Transfer Content <span className="text-muted font-weight-normal">(Nội dung CK)</span></label>
                      <input type="text" className="form-control form-control-sm" name="transferContent" value={formData.transferContent || ''} onChange={handleInputChange} />
                    </div>
                  </div>

                  {/* --- Section 3: Financial & Invoice Details --- */}
                  <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Financial & Invoice Details (Chứng từ & Chi phí)</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label font-weight-bold small">Document Type <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Loại chứng từ)</span></label>
                      <select className={`form-control form-control-sm ${errors.documentType ? "is-invalid" : ""}`} name="documentType" value={formData.documentType || ''} onChange={handleInputChange}>
                        <option value="">(not set)</option>
                        <option value="Hóa đơn VAT">VAT Invoice (Hóa đơn VAT)</option>
                        <option value="Hóa đơn bán hàng">Sales Invoice (Hóa đơn bán hàng)</option>
                        <option value="Không có hóa đơn">No Invoice (Không có hóa đơn)</option>
                      </select>
                      {errors.documentType && <div className="invalid-feedback">{errors.documentType}</div>}
                    </div>

                    <div className="col-md-5 mb-3">
                      <label className="form-label font-weight-bold small">
                        Total Amount <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control form-control-sm ${errors.totalAmount ? 'is-invalid' : ''}`} 
                        name="totalAmount" 
                        value={formatInputAmount(formData.totalAmount)} 
                        onChange={handleAmountChange} 
                      />
                      {errors.totalAmount && (
                        <div className="text-danger" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                          {errors.totalAmount}
                        </div>
                      )}
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label font-weight-bold small">Currency <span className="text-muted font-weight-normal">(Loại tiền)</span></label>
                      <select className="form-control form-control-sm" name="currency" value={formData.currency || 'VND'} onChange={handleInputChange}>
                        <option value="VND">Vietnamese Dong</option>
                        <option value="AUD">Australian Dollar</option>
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    {/* --- KHU VỰC ATTACHED INVOICE (MULTI) --- */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label font-weight-bold small">
                        Attached Invoice {existingFiles?.invoices?.length === 0 && invoiceFiles?.length === 0 && <span className="text-danger">*</span>}
                      </label>
                      <input 
                        type="file" 
                        multiple
                        className={`form-control form-control-sm ${errors.invoice ? 'is-invalid' : ''}`}
                        onChange={(e) => handleFileChange(e, "invoice")} 
                      />
                      {errors.invoice && (
                        <div className="text-danger" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                          {errors.invoice}
                        </div>
                      )}
                      
                      {editingId && existingFiles?.invoices && existingFiles.invoices.length > 0 && (
                        <div className="mt-2">
                          <small className="text-muted d-block font-weight-bold">Saved Invoices:</small>
                          {existingFiles.invoices.map((file, idx) => (
                            <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-1 rounded my-1 border">
                              <button 
                                type="button"
                                onClick={() => handleDownload(getDownloadUrl(file.fileUrl), file.fileName)}
                                className="btn btn-sm btn-link p-0 text-primary small text-truncate max-w-75"
                                style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
                              >
                                <i className="fas fa-file-download mr-1"></i>{file.fileName || `Invoice_${idx + 1}.pdf`}
                              </button>
                              <button 
                                type="button" 
                                className="btn btn-xs p-0 px-1 btn-outline-danger border-0" 
                                onClick={() => setExistingFiles(prev => ({ ...prev, invoices: prev.invoices.filter((_, i) => i !== idx) }))}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {invoiceFiles && invoiceFiles.length > 0 && (
                        <div className="mt-2">
                          <small className="text-success d-block font-weight-bold">New files to upload:</small>
                          {invoiceFiles.map((file, idx) => (
                            <div key={idx} className="d-flex align-items-center justify-content-between badge-light text-dark p-1 rounded my-1 font-weight-normal" style={{ fontSize: '0.75rem' }}>
                              <span className="text-truncate mr-2"><i className="fas fa-paperclip text-success mr-1"></i>{file.name}</span>
                              <span className="text-danger style-pointer" onClick={() => handleRemoveQueuedFile(idx, "invoice")} style={{ cursor: 'pointer' }}>
                                <i className="fas fa-trash-alt"></i>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* --- KHU VỰC PAYMENT STATEMENT (MULTI) --- */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label font-weight-bold small">Payment Statement</label>
                      <input 
                        type="file" 
                        multiple
                        className={`form-control form-control-sm ${errors.paymentStatement ? 'is-invalid' : ''}`}
                        onChange={(e) => handleFileChange(e, "statement")} 
                      />
                      {errors.paymentStatement && (
                        <div className="text-danger" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                          {errors.paymentStatement}
                        </div>
                      )}
                      
                      {editingId && existingFiles?.statements && existingFiles.statements.length > 0 && (
                       <div className="mt-2">
                          <small className="text-muted d-block font-weight-bold">Saved statements:</small>
                          {existingFiles.statements.map((file, idx) => (
                            <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-1 rounded my-1 border">
                              <button 
                                type="button"
                                onClick={() => handleDownload(getDownloadUrl(file.fileUrl), file.fileName)}
                                className="btn btn-sm btn-link p-0 text-primary small text-truncate max-w-75"
                                style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
                              >
                                <i className="fas fa-file-download mr-1"></i>{file.fileName || `Invoice_${idx + 1}.pdf`}
                              </button>
                              <button 
                                type="button" 
                                className="btn btn-xs p-0 px-1 btn-outline-danger border-0" 
                                onClick={() => setExistingFiles(prev => ({ ...prev, statements: prev.statements.filter((_, i) => i !== idx) }))}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {statementFiles && statementFiles.length > 0 && (
                        <div className="mt-2">
                          <small className="text-success d-block font-weight-bold">New files to upload:</small>
                          {statementFiles.map((file, idx) => (
                            <div key={idx} className="d-flex align-items-center justify-content-between badge-light text-dark p-1 rounded my-1 font-weight-normal" style={{ fontSize: '0.75rem' }}>
                              <span className="text-truncate mr-2"><i className="fas fa-paperclip text-success mr-1"></i>{file.name}</span>
                              <span className="text-danger style-pointer" onClick={() => handleRemoveQueuedFile(idx, "statement")} style={{ cursor: 'pointer' }}>
                                <i className="fas fa-trash-alt"></i>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label font-weight-bold small">Internal Document Type <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(Loại chứng từ nội bộ)</span></label>
                      <select className={`form-control form-control-sm ${errors.internalDocumentType ? "is-invalid" : ""}`} name="internalDocumentType" value={formData.internalDocumentType || ''} onChange={handleInputChange}>
                        <option value="">(not set)</option>
                        <option value="Tạm ứng">Advance Payment (Tạm ứng)</option>
                        <option value="Thanh toán">Payment (Thanh toán)</option>
                        <option value="Hoàn ứng">Advance Settlement (Hoàn ứng)</option>
                        <option value="Khác">Others (Khác)</option>
                      </select>
                      {errors.internalDocumentType && <div className="invalid-feedback">{errors.internalDocumentType}</div>}
                    </div>
                  </div>

                </form>
              </div>

              <div className="modal-footer bg-light py-2">
                <button type="submit" form="paymentForm" className="btn btn-success btn-sm px-3" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : editingId ? "Update Changes" : "Save & Submit"}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary btn-sm px-3" 
                  onClick={() => { setShowCreateModal(false); setEditingId(null); }} 
                  disabled={isSubmitting}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;